import React, { useEffect, useState, useMemo } from "react";
import { useAppSelector, useAppDispatch } from "../../redux/hooks";
import { useGetLocationsQuery } from "../../redux/slices/api.slice";
import { useGetFavorites } from "../../hooks/useGetFavorites";

import { setBreadcrumb } from "../../redux/slices/breadCrumb.slice";
import { Tooltip } from "@mantine/core";
import { MdErrorOutline } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import LocationIcon from "../../svg/locationIcon";
import AddressIcon from "../../svg/addressIcon";
import { useGetFavFloorsQuery } from "../../redux/slices/api.slice";
import SiteIcon from "../../svg/addressIcon";
import FloorIcon from "../../svg/floorIcon";
import ClockIcon from "../../svg/clockIcon";
import ExpanIcon from "../../svg/expandIcon";
import ChairIcon from "../../svg/chairIcon";
import WifiIcon from "../../svg/wifiIcon";
import CupIcon from "../../svg/coffeCupIcon";
import MettingsIcon from "../../svg/meetingIocn";
import PrinterIcon from "../../svg/printerIcon";
import WheelChairIcon from "../../svg/weelchairIcon";
import PetIcon from "../../svg/petIcon";
import CouchIcon from "../../svg/couchIcon";
import MuteIcon from "../../svg/muteIcon";
import { GoBookmarkSlash } from "react-icons/go";
import Container from "../../components/Container";
import "../../components/styles/common.css";

const Bookmark = () => {
  const amenitiesData = [
    { label: "Wi-Fi Available", icon: WifiIcon },
    { label: "Coffee Space", icon: CupIcon },
    { label: "Meetings Room", icon: MettingsIcon },
    { label: "Printers", icon: PrinterIcon },
    { label: "Wheelchair Accessible", icon: WheelChairIcon },
    { label: "Pet-friendly", icon: PetIcon },
    { label: "Lounge Area", icon: CouchIcon },
    { label: "Quiet Zones", icon: MuteIcon },
  ];
  const dispatch = useAppDispatch();
  const [favorites, setFavorites] = useGetFavorites(
    "favorite-locations/floor",
    { locations: [], floors: {} }
  );
  const {
    data: locations,
    isLoading: isLoadingLocations,
    isError: isLoactionsError,
  } = useGetLocationsQuery("");

  const {
    data: floors,
    isLoading: isLoadingFloors,
    isError: isFloorsError,
  } = useGetFavFloorsQuery(Object.keys(favorites.floors));

  useEffect(() => {
    dispatch(setBreadcrumb([]));
  }, [dispatch]);

  const favoriteFloors = useMemo(() => {
    const favFloors = Object.values(favorites.floors).flat();
    return floors?.filter((floor) => favFloors.includes(floor.id)) || [];
  }, [floors, favorites.floors]);

  return (
    <Container>
      <div className="flex flex-col gap-5  ">
        <div className="flex flex-col min-h-[600px]">
          <div>
            <h1 className="mt-2 flex gap-4 items-center">
              <SiteIcon width={30} height={30} fill="black" />
              Favorites Sites
            </h1>
            <p className="text-md text-grayText3 mt-3">
              Your list of all preferred sites
            </p>
          </div>
          <div className={`my-10 flex-1 `}>
            {isLoactionsError ? (
              <Error />
            ) : isLoadingLocations ? (
              <Loader height="500px" />
            ) : favorites.locations.length === 0 ? (
              <NoFavorites type="sites" />
            ) : (
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3  xl:grid-cols-4 gap-10 h-full ">
                {favorites.locations.map((location) => {
                  const locData = locations.find((loc) => loc.id === location);

                  return (
                    locData && <LocationCard key={location} locData={locData} />
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className="min-h-[600px] flex flex-col ">
          <div>
            <h1 className="flex items-center gap-4">
              <FloorIcon width={30} height={30} fill="black" />
              Favorites Floors
            </h1>
            <p className="text-base text-grayText3 mt-3">
              Your list of all preferred floors
            </p>
          </div>
          <div className={`my-10 flex-1`}>
            {isFloorsError ? (
              <Error />
            ) : isLoadingFloors ? (
              <Loader height="350px" />
            ) : favoriteFloors.length === 0 ? (
              <NoFavorites type="floors" />
            ) : (
              <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3  xl:grid-cols-4 gap-10">
                {favoriteFloors.map((floor) => {
                  return (
                    <FloorCard
                      key={floor.id}
                      floorData={floor}
                      amenitiesData={amenitiesData}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default Bookmark;

const LocationCard = ({ locData }) => {
  const navigate = useNavigate();
  const workingHours = useMemo(() => {
    return locData.openingTime
      ? Object.entries(locData.openingTime)
          .map(([day, times]) => {
            if (times) {
              const [startTime, endTime] = (times as string).split("-");
              const formattedStartTime = startTime && `${startTime.trim()}`;
              const formattedEndTime = endTime && `${endTime.trim()}`;
              return {
                day: day.slice(0, 3),
                time: `${formattedStartTime} - ${formattedEndTime}`,
              };
            }
            return null;
          })
          .filter((el) => el !== null)
      : [];
  }, [locData.openingTime]);

  return (
    <div className="relative flex flex-col rounded-3xl w-full min-h-[420px] border-grayText4 border border-solid bg-white ">
      <div className="w-full h-52 cursor-pointer ">
        {locData.imageURL ? (
          <img
            width="100%"
            height="100%"
            className="rounded-t-[20px]"
            src={locData.imageURL}
            alt={locData.name}
          />
        ) : (
          <div className="w-full rounded-t-[20px]  bg-grayText4 h-full  flex items-center justify-center">
            <AddressIcon width="80px" height="60px" />
          </div>
        )}
      </div>

      <div className="flex flex-col gap-5 px-8 pt-4 pb-5 flex-1">
        <h1 className="font-medium text-2xl text-black">{locData.name}</h1>
        <div className="bg-grayText4 h-[1.2px] w-full " />

        <div className="flex flex-col gap-2 flex-1 justify-center ">
          <div className="flex gap-3 items-center w-full">
            <LocationIcon />

            <p className="text-grayText3 text-md max-w-full flex-1  whitespace-nowrap overflow-hidden text-ellipsis">
              {locData.address}
            </p>
            <a
              target="_blank"
              rel="noreferrer"
              href={`https://www.google.com/maps?q=[${locData.address}]`}
            >
              <ExpanIcon className="text-action cursor-pointer" />
            </a>
          </div>

          <div className="flex gap-3 items-center">
            <ClockIcon />

            {workingHours.length > 0 ? (
              <Tooltip
                label={workingHours.map((el, index) => {
                  return (
                    <p key={index} className="text-white text-md flex-1">
                      {`${el.day} ${el.time}`}
                    </p>
                  );
                })}
                withArrow
              >
                <div className="flex-1 text-grayText2  text-ellipsis  whitespace-nowrap overflow-hidden max-w-full">
                  {workingHours.slice(0, 3).map((el, index) => {
                    return (
                      <span
                        key={index + Date.now()}
                        className="text-grayText2 text-md"
                      >
                        {` ${el.day} ${el.time}`},
                      </span>
                    );
                  })}
                </div>
              </Tooltip>
            ) : (
              <Tooltip
                label={
                  <p className="text-white text-md flex-1">
                    Everyday, 09:00AM - 05:00PM
                  </p>
                }
                withArrow
              >
                <p className="flex-1 text-grayText2 text-md text-ellipsis  whitespace-nowrap overflow-hidden max-w-full">
                  Everyday, 09:00AM - 05:00PM
                </p>
              </Tooltip>
            )}
          </div>
          <div className="flex gap-3 items-center">
            <AddressIcon width="20px" height="19px" />
            <p className="text-grayText3 text-md flex-1 text-ellipsis  whitespace-nowrap overflow-hidden ">
              {`${locData.floors} Total floors - ${locData.desks} Desks`}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            navigate(`/Location/${locData.id}`);
          }}
          className="  hover:bg-action rounded-[100px] font-md  hover:text-white py-3 text-md bg-actionLight text-action"
        >
          See Floors & Desks
        </button>
      </div>
    </div>
  );
};

const FloorCard = ({ floorData, amenitiesData }) => {
  const navigate = useNavigate();
  const availbleDesks =
    floorData.capacity - floorData.occupied - floorData.unavailable;

  return (
    <div className="relative  flex flex-col rounded-3xl px-5 py-4   w-full  border-grayText4 border border-solid bg-white ">
      <div className="flex gap-2 items-center">
        <div className="relative w-16 h-16 rounded-[50%] bg-lightGray flex justify-center items-center">
          <AddressIcon width="35px" height="35px" />
          <div
            className={`h-5 w-5 border-4 border-white border-solid rounded-[50%] ${
              floorData.live ? "bg-success" : "bg-alert"
            } absolute bottom-0 -right-1`}
          />
        </div>

        <h1 className="font-md text-black text-[22px]  flex-1  ">
          {floorData.name}
        </h1>
      </div>
      <div className="bg-grayText4 h-[1.3px] w-full mt-3 " />

      <div className="flex flex-col  justify-center  my-5 flex-1">
        <div className="flex gap-2 my-3 text-grayText3  items-center ">
          <ChairIcon />
          <p>
            <span
              className={` mr-1 font-md ${
                availbleDesks ? "text-success" : "text-actionLight2"
              }`}
            >
              {availbleDesks}
            </span>
            of {floorData.capacity} Desks Available
          </p>
        </div>

        <div className="flex gap-2  items-center max-w-full flex-wrap flex-1  ">
          {floorData.amenities?.map((am) => {
            const icon = amenitiesData.find((el) => el.label === am)?.icon;
            return <IconComp key={am} content={am} Icon={icon} />;
          })}
        </div>
      </div>
      <div className="flex  items-end gap-4  h-fit">
        <button
          onClick={() => {
            navigate(`/Location/${floorData.locationID}/${floorData.id}`);
          }}
          className="flex-1 hover:bg-action rounded-[100px] font-md  hover:text-white py-3 text-md bg-actionLight text-action"
        >
          View Floor Plan
        </button>
      </div>
    </div>
  );
};
const IconComp = ({ content, Icon }) => {
  return (
    <Tooltip label={content} withArrow>
      <div className="p-3 rounded-[50%] bg-lightGray flex justify-center items-center">
        <Icon />
      </div>
    </Tooltip>
  );
};

const Error = () => {
  return (
    <div className="flex px-4 items-center justify-center   h-[430px]  border border-solid rounded-[10px] border-grayText4">
      <div className="flex items-center justify-center flex-col  gap-2  ">
        <MdErrorOutline className="text-4xl" />
        <div className="text-center">
          <p className="text-2xl font-semibold">Error</p>
          <p className="text-base sm:text-lg text-grayText3   max-w-[500px] ">
            Something went wrong !
          </p>
        </div>
      </div>
    </div>
  );
};

const NoFavorites = ({ type }) => {
  return (
    <div className="flex items-center justify-center px-4  h-[430px]  border border-solid rounded-[10px] border-grayText4">
      <div className="flex items-center justify-center flex-col gap-3 ">
        <GoBookmarkSlash className="text-5xl" />
        <p className="text-xl font-semibold">No Favorites</p>
        <p className="text-sm sm:text-base text-grayText3   max-w-[500px] text-center">
          {`There are no favorite ${type} saved. Start exploring and selecting your
          preferred ${type} to build your list of favorites!`}
        </p>
      </div>
    </div>
  );
};

const Loader = ({ height }) => {
  return (
    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3  xl:grid-cols-4 gap-5 h-full">
      {Array.from({ length: 4 }, (_, index) => {
        return (
          <div
            className={`loader rounded-3xl w-full min-h-[${height}]  bg-[#8896ab]`}
            key={index + 1}
          />
        );
      })}
    </div>
  );
};
